import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ContractRecord, CustomerRecord, InvoiceRecord, LeadRecord, PaymentOrderRecord, ProjectRecord, TaskRecord } from '@shared/types';
import { formatInvoiceReference } from '@shared/utils/invoices';
import { dispatchShellDomainInvalidated } from '@renderer/app/events/shell-domain-events';
import { Icon } from '@renderer/shared/components/icons';
import { FilterChip, IconButton } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { listDeletedContracts, purgeContracts, restoreContracts } from '@renderer/domains/contracts/contracts-repository';
import { listDeletedCustomers, purgeCustomers, restoreCustomers } from '@renderer/domains/customers/customers-repository';
import { listDeletedInvoices, purgeInvoices, restoreInvoices } from '@renderer/domains/invoices/invoices-repository';
import { listDeletedLeads, purgeLeads, restoreLeads } from '@renderer/domains/leads/leads-repository';
import { listDeletedPaymentOrders, purgePaymentOrders, restorePaymentOrders } from '@renderer/domains/payments/payments-repository';
import { listDeletedProjects, purgeProjects, restoreProjects } from '@renderer/domains/projects/projects-repository';
import { listDeletedTasks, purgeTasks, restoreTasks } from '@renderer/domains/tasks/tasks-repository';

type TrashDomain = 'projects' | 'leads' | 'tasks' | 'customers' | 'contracts' | 'invoices' | 'payments';
type TrashFilter = 'all' | TrashDomain;

type TrashItem = {
  key: string;
  domain: TrashDomain;
  id: string;
  title: string;
  subtitle: string | null;
  deletedAt: string;
};

const purgeDomainOrder: TrashDomain[] = ['tasks', 'payments', 'invoices', 'contracts', 'leads', 'projects', 'customers'];
const allTrashDomains: TrashDomain[] = ['projects', 'leads', 'tasks', 'customers', 'contracts', 'invoices', 'payments'];

function asTrashItems({
  projects,
  leads,
  tasks,
  customers,
  contracts,
  invoices,
  payments
}: {
  projects: ProjectRecord[];
  leads: LeadRecord[];
  tasks: TaskRecord[];
  customers: CustomerRecord[];
  contracts: ContractRecord[];
  invoices: InvoiceRecord[];
  payments: PaymentOrderRecord[];
}): TrashItem[] {
  const items: TrashItem[] = [];

  for (const project of projects) {
    if (!project.deletedAt) {
      continue;
    }

    items.push({
      key: `projects:${project.id}`,
      domain: 'projects',
      id: project.id,
      title: project.name,
      subtitle: project.slug,
      deletedAt: project.deletedAt
    });
  }

  for (const lead of leads) {
    if (!lead.deletedAt) {
      continue;
    }

    items.push({
      key: `leads:${lead.id}`,
      domain: 'leads',
      id: lead.id,
      title: lead.name,
      subtitle: lead.currentWebsiteUrl ?? lead.phone ?? lead.slug,
      deletedAt: lead.deletedAt
    });
  }

  for (const task of tasks) {
    if (!task.deletedAt) {
      continue;
    }

    items.push({
      key: `tasks:${task.id}`,
      domain: 'tasks',
      id: task.id,
      title: task.title,
      subtitle: task.dueAt,
      deletedAt: task.deletedAt
    });
  }

  for (const customer of customers) {
    if (!customer.deletedAt) {
      continue;
    }

    items.push({
      key: `customers:${customer.id}`,
      domain: 'customers',
      id: customer.id,
      title: customer.name,
      subtitle: customer.slug,
      deletedAt: customer.deletedAt
    });
  }

  for (const contract of contracts) {
    if (!contract.deletedAt) {
      continue;
    }

    items.push({
      key: `contracts:${contract.id}`,
      domain: 'contracts',
      id: contract.id,
      title: contract.title,
      subtitle: contract.contractNumber,
      deletedAt: contract.deletedAt
    });
  }

  for (const invoice of invoices) {
    if (!invoice.deletedAt) {
      continue;
    }

    items.push({
      key: `invoices:${invoice.id}`,
      domain: 'invoices',
      id: invoice.id,
      title: formatInvoiceReference(invoice.series, invoice.number),
      subtitle: invoice.customerSnapshot.legalName,
      deletedAt: invoice.deletedAt
    });
  }

  for (const payment of payments) {
    if (!payment.deletedAt) {
      continue;
    }

    items.push({
      key: `payments:${payment.id}`,
      domain: 'payments',
      id: payment.id,
      title: payment.title,
      subtitle: payment.publicUrl,
      deletedAt: payment.deletedAt
    });
  }

  return items.sort((left, right) => new Date(right.deletedAt).getTime() - new Date(left.deletedAt).getTime());
}

function getItemIdsByDomain(items: TrashItem[]): Record<TrashDomain, string[]> {
  const idsByDomain: Record<TrashDomain, string[]> = {
    projects: [],
    leads: [],
    tasks: [],
    customers: [],
    contracts: [],
    invoices: [],
    payments: []
  };

  for (const item of items) {
    idsByDomain[item.domain].push(item.id);
  }

  return idsByDomain;
}

function dispatchInvalidatedDomains(domains: Iterable<TrashDomain>): void {
  const invalidatedDomains = new Set(domains);

  if (invalidatedDomains.has('customers')) {
    for (const domain of allTrashDomains) {
      invalidatedDomains.add(domain);
    }
  }

  for (const domain of invalidatedDomains) {
    dispatchShellDomainInvalidated(domain);
  }
}

export function TrashModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }): JSX.Element | null {
  const { locale, t } = useI18n();
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TrashFilter>('all');
  const [items, setItems] = useState<TrashItem[]>([]);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const counts = useMemo(() => {
    const byDomain: Record<TrashDomain, number> = {
      projects: 0,
      leads: 0,
      tasks: 0,
      customers: 0,
      contracts: 0,
      invoices: 0,
      payments: 0
    };

    for (const item of items) {
      byDomain[item.domain] += 1;
    }

    return {
      all: items.length,
      ...byDomain
    };
  }, [items]);

  const visibleItems = useMemo(() => {
    if (filter === 'all') {
      return items;
    }

    return items.filter((item) => item.domain === filter);
  }, [filter, items]);

  const formatDeletedAt = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
    return (value: string) => formatter.format(new Date(value));
  }, [locale]);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    setStatus('loading');

    try {
      const [projects, leads, tasks, customers, contracts, invoices, payments] = await Promise.all([
        listDeletedProjects(),
        listDeletedLeads(),
        listDeletedTasks(),
        listDeletedCustomers(),
        listDeletedContracts(),
        listDeletedInvoices(),
        listDeletedPaymentOrders()
      ]);

      setItems(asTrashItems({ projects, leads, tasks, customers, contracts, invoices, payments }));
      setStatus('ready');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Trash unavailable.');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void reload();
  }, [isOpen, reload]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  async function handleRestore(item: TrashItem): Promise<void> {
    setError(null);
    setBusyKey(item.key);

    try {
      switch (item.domain) {
        case 'projects':
          await restoreProjects({ ids: [item.id] });
          break;
        case 'leads':
          await restoreLeads({ ids: [item.id] });
          break;
        case 'tasks':
          await restoreTasks({ ids: [item.id] });
          break;
        case 'customers':
          await restoreCustomers({ ids: [item.id] });
          break;
        case 'contracts':
          await restoreContracts({ ids: [item.id] });
          break;
        case 'invoices':
          await restoreInvoices({ ids: [item.id] });
          break;
        case 'payments':
          await restorePaymentOrders({ ids: [item.id] });
          break;
      }

      dispatchInvalidatedDomains([item.domain]);
      await reload();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Restore failed.');
    } finally {
      setBusyKey(null);
    }
  }

  async function handlePurge(item: TrashItem): Promise<void> {
    if (!window.confirm(t('trash.confirmDeleteForever'))) {
      return;
    }

    setError(null);
    setBusyKey(item.key);

    try {
      switch (item.domain) {
        case 'projects':
          await purgeProjects({ ids: [item.id] });
          break;
        case 'leads':
          await purgeLeads({ ids: [item.id] });
          break;
        case 'tasks':
          await purgeTasks({ ids: [item.id] });
          break;
        case 'customers':
          await purgeCustomers({ ids: [item.id] });
          break;
        case 'contracts':
          await purgeContracts({ ids: [item.id] });
          break;
        case 'invoices':
          await purgeInvoices({ ids: [item.id] });
          break;
        case 'payments':
          await purgePaymentOrders({ ids: [item.id] });
          break;
      }

      dispatchInvalidatedDomains([item.domain]);
      await reload();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Permanent delete failed.');
    } finally {
      setBusyKey(null);
    }
  }

  async function handleBulkPurge(targetItems: TrashItem[], mode: 'all' | 'filter'): Promise<void> {
    if (targetItems.length === 0) {
      return;
    }

    const confirmKey = mode === 'all' ? 'trash.confirmClearTrash' : 'trash.confirmClearTab';
    if (!window.confirm(t(confirmKey, { count: targetItems.length }))) {
      return;
    }

    setError(null);
    setBusyKey(`bulk:${mode}`);

    try {
      const idsByDomain = getItemIdsByDomain(targetItems);

      for (const domain of purgeDomainOrder) {
        const ids = idsByDomain[domain];
        if (ids.length === 0) {
          continue;
        }

        switch (domain) {
          case 'projects':
            await purgeProjects({ ids });
            break;
          case 'leads':
            await purgeLeads({ ids });
            break;
          case 'tasks':
            await purgeTasks({ ids });
            break;
          case 'customers':
            await purgeCustomers({ ids });
            break;
          case 'contracts':
            await purgeContracts({ ids });
            break;
          case 'invoices':
            await purgeInvoices({ ids });
            break;
          case 'payments':
            await purgePaymentOrders({ ids });
            break;
        }
      }

      dispatchInvalidatedDomains(targetItems.map((item) => item.domain));
      await reload();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Permanent delete failed.');
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div
      className="av-trash-modal__overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        aria-labelledby="trash-title"
        aria-modal="true"
        className="av-trash-modal"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="av-trash-modal__header">
          <h2 id="trash-title">{t('trash.title')}</h2>
          <button className="av-trash-modal__close" type="button" onClick={onClose}>
            <Icon name="times" />
          </button>
        </div>

        <div className="av-trash-modal__toolbar">
          <div className="av-trash-modal__filters">
            <FilterChip
              active={filter === 'all'}
              label={t('trash.filters.all', { count: counts.all })}
              onClick={() => setFilter('all')}
            />
            <FilterChip
              active={filter === 'projects'}
              icon="projects"
              label={t('trash.filters.projects', { count: counts.projects })}
              onClick={() => setFilter('projects')}
            />
            <FilterChip
              active={filter === 'leads'}
              icon="leads"
              label={t('trash.filters.leads', { count: counts.leads })}
              onClick={() => setFilter('leads')}
            />
            <FilterChip
              active={filter === 'tasks'}
              icon="tasks"
              label={t('trash.filters.tasks', { count: counts.tasks })}
              onClick={() => setFilter('tasks')}
            />
            <FilterChip
              active={filter === 'customers'}
              icon="customers"
              label={t('trash.filters.customers', { count: counts.customers })}
              onClick={() => setFilter('customers')}
            />
            <FilterChip
              active={filter === 'contracts'}
              icon="contracts"
              label={t('trash.filters.contracts', { count: counts.contracts })}
              onClick={() => setFilter('contracts')}
            />
            <FilterChip
              active={filter === 'invoices'}
              icon="invoices"
              label={t('trash.filters.invoices', { count: counts.invoices })}
              onClick={() => setFilter('invoices')}
            />
            <FilterChip
              active={filter === 'payments'}
              icon="payments"
              label={t('trash.filters.payments', { count: counts.payments })}
              onClick={() => setFilter('payments')}
            />
          </div>

          <div className="av-trash-modal__bulk-actions">
            {filter !== 'all' ? (
              <button
                className="av-secondary-button av-trash-modal__bulk-button"
                disabled={busyKey !== null || visibleItems.length === 0}
                type="button"
                onClick={() => void handleBulkPurge(visibleItems, 'filter')}
              >
                {t('trash.actions.clearTab')}
              </button>
            ) : null}
            <button
              className="av-danger-button av-trash-modal__bulk-button"
              disabled={busyKey !== null || items.length === 0}
              type="button"
              onClick={() => void handleBulkPurge(items, 'all')}
            >
              {t('trash.actions.clearTrash')}
            </button>
          </div>
        </div>

        {status === 'loading' ? <div className="av-trash-modal__state">{t('trash.loading')}</div> : null}
        {status === 'error' ? <div className="av-trash-modal__state">{error}</div> : null}
        {status === 'ready' && error ? <div className="av-trash-modal__error">{error}</div> : null}

        {status === 'ready' && visibleItems.length === 0 ? (
          <div className="av-trash-modal__state">
            <strong>{t('trash.emptyTitle')}</strong>
            <p>{t('trash.emptyDescription')}</p>
          </div>
        ) : null}

        {status === 'ready' && visibleItems.length > 0 ? (
          <div className="av-trash-modal__list">
            {visibleItems.map((item) => (
              <div key={item.key} className="av-trash-row">
                <div className="av-trash-row__icon">
                  <Icon name={item.domain} />
                </div>
                <div className="av-trash-row__copy">
                  <strong>{item.title}</strong>
                  {item.subtitle ? <span>{item.subtitle}</span> : null}
                </div>
                <div className="av-trash-row__meta">{formatDeletedAt(item.deletedAt)}</div>
                <div className="av-trash-row__actions">
                  <IconButton
                    className="av-trash-row__action av-trash-row__action--restore"
                    disabled={busyKey !== null}
                    icon="restore"
                    label={t('trash.actions.restore')}
                    onClick={() => void handleRestore(item)}
                  />
                  <IconButton
                    className="av-trash-row__action av-trash-row__action--purge"
                    disabled={busyKey !== null}
                    icon="trash"
                    label={t('trash.actions.deleteForever')}
                    onClick={() => void handlePurge(item)}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
